import { useEffect, useState } from 'react'

/**
 * Debounces a value by delaying its update until after a specified delay period.
 * Useful for search inputs to reduce API calls.
 *
 * @param value - The value to debounce
 * @param delay - The delay in milliseconds (default: 300ms)
 * @returns The debounced value
 *
 * @example
 * ```tsx
 * const [searchText, setSearchText] = useState('');
 * const debouncedSearchText = useDebounce(searchText, 300);
 *
 * // Use debouncedSearchText for API calls
 * useEffect(() => {
 *   if (debouncedSearchText) {
 *     fetchResults(debouncedSearchText);
 *   }
 * }, [debouncedSearchText]);
 * ```
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    // Set up a timer to update the debounced value after the delay
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    // Clean up the timeout if value changes before delay expires
    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}
